import { lazy, Suspense, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router';

import {
  useApplyUiScale,
  useShortcutBindings,
  useShortcutsEnabled,
  useViewer,
} from '@/entities/viewer';
import { areaForPath } from '@/entities/feedback';
import { ConnectionNotice, RealtimeProvider } from '@/entities/realtime';
import { NotificationBell } from '@/features/notifications';
import { ShortcutHelp } from '@/features/shortcut-help';
import { useIsMobile } from '@/shared/lib/media';
import { returnStateFrom } from '@/shared/lib/return-target';
import { bindingText, useShortcutHandler, useShortcutListener } from '@/shared/lib/shortcuts';
import { type KindFilter, MarkdownLinkDialog, QuickNavDialog } from '@/features/quick-nav';
import {
  Icons,
  MarkdownLinkPickerContext,
  type MarkdownLinkPickerProps,
  Popover,
} from '@/shared/ui';

import styles from './AppLayout.module.css';

// Mysliarium pulls in the Markdown renderer: it is loaded the first time the drawer opens.
const MysliariumDrawer = lazy(() =>
  import('@/features/mysliarium').then((module) => ({ default: module.MysliariumDrawer })),
);

/** Authenticated application shell. Pages control their own width (boards are full-width). */
const renderMarkdownLinkDialog = (props: MarkdownLinkPickerProps) => (
  <MarkdownLinkDialog {...props} />
);

export function AppLayout() {
  const { t } = useTranslation();
  const viewer = useViewer();
  useApplyUiScale();
  // Mysliarium (personal notes) is reachable from every page; it keeps the page behind it as is.
  const [notesOpen, setNotesOpen] = useState(false);
  const notesButton = useRef<HTMLButtonElement>(null);
  const boardMatch = useMatch('/app/boards/:boardId/*');
  // Settings opened from a board / card remember where to return to.
  const location = useLocation();
  const returnState = returnStateFrom(location);
  const navigate = useNavigate();
  const mobile = useIsMobile();
  // The lightbulb carries one whitelisted section name (never the address) as a usability hint.
  const area = areaForPath(location.pathname);
  const feedbackState = area ? { feedbackArea: area } : undefined;
  const [helpOpen, setHelpOpen] = useState(false);
  const closeHelp = useCallback(() => {
    setHelpOpen(false);
  }, []);
  const bindings = useShortcutBindings();
  const shortcutsEnabled = useShortcutsEnabled();
  useShortcutListener(bindings, shortcutsEnabled);
  useShortcutHandler('help', () => {
    setHelpOpen((open) => !open);
  });
  useShortcutHandler('go-workspace', () => {
    void navigate('/app');
  });
  // The ONE resource switcher: the navbar button, Ctrl/Cmd+K and the board shortcut (G B, which
  // starts on boards) all open the same dialog - on every page, so the key never means "search
  // this page" in some places and "go anywhere" in others. Local searches keep their visible boxes.
  const [quickOpen, setQuickOpen] = useState<KindFilter | null>(null);
  useShortcutHandler('search', () => {
    setQuickOpen('all');
  });
  useShortcutHandler('switch-board', () => {
    setQuickOpen('board');
  });
  const quickNavButton = (
    <button
      type="button"
      className={styles.switcher}
      aria-label={t('quickNav.open')}
      title={`${t('quickNav.open')} (${bindingText(bindings['search'])})`}
      aria-haspopup="dialog"
      data-testid="quick-nav-button"
      onClick={() => {
        setQuickOpen('all');
      }}
    >
      <Icons.switcher />
      <span className={styles.switcherText}>{t('quickNav.switcher')}</span>
      <kbd className={styles.switcherKey} aria-hidden>
        {bindingText(bindings['search'])}
      </kbd>
    </button>
  );
  const notesToggle = (
    <button
      ref={notesButton}
      type="button"
      className={`${styles.iconLink} ${notesOpen ? styles.iconOn : ''}`}
      aria-label={t('mysliarium.open')}
      title={t('mysliarium.open')}
      aria-pressed={notesOpen}
      aria-controls={notesOpen ? 'mysliarium' : undefined}
      data-testid="mysliarium-button"
      onClick={() => {
        setNotesOpen((open) => !open);
      }}
    >
      <span aria-hidden>💭</span>
    </button>
  );
  const accountLink = (
    <NavLink
      to="/app/account"
      {...(returnState && { state: returnState })}
      className={styles.user}
      aria-label={
        viewer.status === 'authenticated'
          ? `${t('nav.account')}: ${viewer.user.display_name}`
          : t('nav.account')
      }
    >
      <span className={styles.avatar} aria-hidden="true">
        {viewer.status === 'authenticated' ? viewer.user.display_name.trim().charAt(0) : ''}
      </span>
      <span className={styles.userName}>
        {viewer.status === 'authenticated' ? viewer.user.display_name : t('nav.account')}
      </span>
    </NavLink>
  );
  return (
    <RealtimeProvider>
      <MarkdownLinkPickerContext value={renderMarkdownLinkDialog}>
        <div className={styles.shell} data-notes={notesOpen ? 'open' : undefined}>
          <header className={styles.header}>
            {/* The brand is the way back to the workspace (there is no separate text link). */}
            <NavLink
              to="/app"
              end
              className={styles.brand}
              aria-label={`${t('app.name')}: ${t('nav.workspace')}`}
              title={t('nav.workspace')}
            >
              {t('app.name')}
            </NavLink>
            {!mobile && (
              <>
                {quickNavButton}
                {notesToggle}
                <NotificationBell />
                <NavLink
                  to="/app/feedback"
                  {...(feedbackState && { state: feedbackState })}
                  className={styles.iconLink}
                  aria-label={t('nav.feedback')}
                  title={t('nav.feedback')}
                  data-testid="feedback-link"
                >
                  <Icons.lightbulb />
                </NavLink>
                <NavLink
                  to="/app/help"
                  className={styles.iconLink}
                  aria-label={t('nav.help')}
                  title={t('nav.help')}
                >
                  <span aria-hidden>?</span>
                </NavLink>
                <NavLink
                  to="/app/settings/interface"
                  {...(returnState && { state: returnState })}
                  className={styles.iconLink}
                  aria-label={t('nav.interface')}
                  title={t('nav.interface')}
                >
                  <span aria-hidden>⚙</span>
                </NavLink>
                {/* The name is the way into Account (profile, password, language, sign out). */}
                {accountLink}
              </>
            )}
            {mobile && (
              <>
                {quickNavButton}
                <NotificationBell />
                <Popover
                  label={t('nav.menu')}
                  width={280}
                  alignEnd
                  trigger={(props, open) => (
                    <button
                      type="button"
                      {...props}
                      className={`${styles.iconLink} ${open ? styles.iconOn : ''}`}
                      aria-label={t('nav.menu')}
                      title={t('nav.menu')}
                      data-testid="nav-menu-button"
                    >
                      <Icons.menu />
                    </button>
                  )}
                >
                  {(close) => (
                    <nav aria-label={t('nav.menu')} className={styles.menu}>
                      <NavLink
                        to="/app/feedback"
                        {...(feedbackState && { state: feedbackState })}
                        className={styles.menuItem}
                        onClick={close}
                      >
                        <Icons.lightbulb /> {t('nav.feedback')}
                      </NavLink>
                      <NavLink to="/app/help" className={styles.menuItem} onClick={close}>
                        <span className={styles.menuGlyph} aria-hidden>
                          ?
                        </span>{' '}
                        {t('nav.help')}
                      </NavLink>
                      <NavLink
                        to="/app/settings/interface"
                        {...(returnState && { state: returnState })}
                        className={styles.menuItem}
                        onClick={close}
                      >
                        <span className={styles.menuGlyph} aria-hidden>
                          ⚙
                        </span>{' '}
                        {t('nav.interface')}
                      </NavLink>
                      <button
                        type="button"
                        className={`${styles.menuItem} ${notesOpen ? styles.menuOn : ''}`}
                        aria-pressed={notesOpen}
                        onClick={() => {
                          setNotesOpen((open) => !open);
                          close();
                        }}
                      >
                        <span className={styles.menuGlyph} aria-hidden>
                          💭
                        </span>{' '}
                        {t('mysliarium.open')}
                      </button>
                      <NavLink
                        to="/app/account"
                        {...(returnState && { state: returnState })}
                        className={styles.menuItem}
                        onClick={close}
                      >
                        <span className={styles.avatar} aria-hidden="true">
                          {viewer.status === 'authenticated'
                            ? viewer.user.display_name.trim().charAt(0)
                            : ''}
                        </span>
                        <span className={styles.menuText}>
                          {viewer.status === 'authenticated'
                            ? `${t('nav.account')}: ${viewer.user.display_name}`
                            : t('nav.account')}
                        </span>
                      </NavLink>
                    </nav>
                  )}
                </Popover>
              </>
            )}
          </header>
          <ConnectionNotice />
          <main className={styles.main}>
            <Outlet />
          </main>
          {helpOpen && <ShortcutHelp bindings={bindings} onClose={closeHelp} />}
          {quickOpen && (
            <QuickNavDialog
              initialKind={quickOpen}
              onClose={() => {
                setQuickOpen(null);
              }}
            />
          )}
          {notesOpen && (
            <div id="mysliarium">
              <Suspense fallback={null}>
                <MysliariumDrawer
                  currentBoardId={boardMatch?.params['boardId'] ?? null}
                  onClose={() => {
                    setNotesOpen(false);
                    notesButton.current?.focus();
                  }}
                />
              </Suspense>
            </div>
          )}
        </div>
      </MarkdownLinkPickerContext>
    </RealtimeProvider>
  );
}
